import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Fuel, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const linkBase = "text-sm font-semibold tracking-wide uppercase transition-colors";

export default function Navbar() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const doLogout = async () => {
        await logout();
        navigate("/");
    };

    const links = [
        { to: "/", label: "Home" },
        { to: "/order", label: "Order Diesel" },
        { to: "/coverage", label: "Coverage" },
        { to: "/about", label: "About" },
        { to: "/contact", label: "Contact" },
    ];

    return (
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="site-navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                    <div className="w-9 h-9 bg-blue-700 flex items-center justify-center rounded-sm">
                        <Fuel className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="leading-none">
                        <div className="font-heading font-black text-slate-900 tracking-tight text-lg">VAISHALI FUELS</div>
                        <div className="text-[10px] font-mono-plex text-slate-500 uppercase tracking-widest">Bharat Petroleum Dealer · Vadodara</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? "text-blue-700" : "text-slate-700 hover:text-blue-700"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="tel:+919512477777"
                        className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
                        data-testid="nav-phone-link"
                    >
                        <Phone className="w-4 h-4" /> +91 95124 77777
                    </a>
                    {user ? (
                        <>
                            {user.role === "admin" ? (
                                <Link to="/admin">
                                    <Button variant="outline" className="rounded-sm border-slate-300" data-testid="nav-admin-btn">Admin</Button>
                                </Link>
                            ) : (
                                <Link to="/my-orders">
                                    <Button variant="outline" className="rounded-sm border-slate-300" data-testid="nav-orders-btn">My Orders</Button>
                                </Link>
                            )}
                            <Button onClick={doLogout} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="nav-logout-btn">Logout</Button>
                        </>
                    ) : (
                        <>
                            <Link to="/login">
                                <Button variant="ghost" className="rounded-sm" data-testid="nav-login-btn">Login</Button>
                            </Link>
                            <Link to="/order">
                                <Button className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-semibold" data-testid="nav-order-btn">
                                    Order Now
                                </Button>
                            </Link>
                        </>
                    )}
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className="lg:hidden p-2 rounded-sm border border-slate-300"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) => `block ${linkBase} ${isActive ? "text-blue-700" : "text-slate-700"}`}
                            data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="pt-3 border-t border-slate-200 flex flex-col gap-2">
                        {user ? (
                            <>
                                {user.role === "admin" ? (
                                    <Link to="/admin" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full rounded-sm" data-testid="nav-mobile-admin-btn">Admin Dashboard</Button>
                                    </Link>
                                ) : (
                                    <Link to="/my-orders" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full rounded-sm" data-testid="nav-mobile-orders-btn">My Orders</Button>
                                    </Link>
                                )}
                                <Button onClick={() => { setOpen(false); doLogout(); }} className="w-full rounded-sm bg-slate-900" data-testid="nav-mobile-logout-btn">Logout</Button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)}>
                                    <Button variant="outline" className="w-full rounded-sm" data-testid="nav-mobile-login-btn">Login</Button>
                                </Link>
                                <Link to="/order" onClick={() => setOpen(false)}>
                                    <Button className="w-full rounded-sm bg-blue-700 hover:bg-blue-800" data-testid="nav-mobile-order-btn">Order Now</Button>
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
